import styled from '@emotion/styled';
import { forwardRef, useMemo, useState } from 'react';

import { Button } from '@travelnest/hui';

import { Stack } from '@/components/ui/Stack';
import { Heading } from '@/components/ui/Text';
import { DialogDrawer } from '@/components/ui/dialog-drawer';
import { getReviewStats, reviewsForPreview } from '@/lib/rating';
import { spacing } from '@/lib/theme';
import { useI18n } from '@/locales';
import type { IReview } from '@/types/content';

import { MoreAction } from '../MoreAction';
import { RatingBreakdown } from './RatingBreakdown';
import { ReviewList } from './ReviewList';

const SHOW_ALL = -1;

/* Jumping here stops just below the divider above, not on the heading's text. */
const Section = styled(Stack)`
	scroll-margin-top: calc(${spacing.space50} + ${spacing.space10});
`;

interface ReviewsPanelProps {
	reviews: IReview[];
}

/**
 * The reviews section: score breakdown, the four most recent reviews, and a
 * modal with all of them. Clicking a preview opens the modal at that review.
 */
export const ReviewsPanel = forwardRef<HTMLDivElement, ReviewsPanelProps>(
	function ReviewsPanel({ reviews }, ref) {
		const t = useI18n();
		const [focused, setFocused] = useState<number | null>(null);
		const stats = useMemo(() => getReviewStats(reviews), [reviews]);
		const preview = useMemo(() => reviewsForPreview(reviews), [reviews]);
		const sorted = useMemo(
			() =>
				[...reviews].sort((a, b) =>
					b.reviewCreatedAt.localeCompare(a.reviewCreatedAt),
				),
			[reviews],
		);
		const close = () => setFocused(null);

		return (
			<Section
				ref={ref}
				gap="space40"
				id="reviews"
				data-testid="reviews-panel"
			>
				<Heading as="h2" size="md">
					{t('property.reviews.title')}
				</Heading>
				<RatingBreakdown stats={stats} />
				<ReviewList reviews={preview} preview onOpen={setFocused} />
				<MoreAction>
					<Button
						text={t('property.reviews.showAll')}
						variant="secondary"
						fill="light"
						size="regular"
						onClick={() => setFocused(SHOW_ALL)}
						data-testid="show-all-reviews-button"
					/>
				</MoreAction>
				<DialogDrawer
					open={focused !== null}
					onOpenChange={(next) => !next && close()}
					title={t('property.reviews.title')}
					closeLabel={t('common.close')}
					desktopWidth="720px"
					mobileFill="full"
				>
					<Stack gap="space50">
						<RatingBreakdown stats={stats} />
						<ReviewList
							reviews={sorted}
							columns={1}
							scrollTo={
								focused !== null && focused !== SHOW_ALL
									? focused
									: null
							}
						/>
					</Stack>
				</DialogDrawer>
			</Section>
		);
	},
);
